import { Grid } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import StationAction from 'station/actions/StationAction'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import DtoObservatoryFollowData from 'station/dto/DtoObservatoryFollowData'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import { countBy, groupBy, keys, maxBy, template, values } from 'lodash'
import ProductionUnitAction from 'productionUnit/actions/ProductionUnitAction'
import DtoObservatoryFollowResult from 'station/dto/DtoObservatoryFollowResult'
import { INDICATORS_COLORS, THRESHOLD_COLORS_CODES } from 'utils/constants/ColorTheme'
import UnitLinkedVolumes from './UnitLinkedVolumes'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import ProgressBar from 'components/progress/ProgressBar'
import moment from 'moment'
import useSandreList from 'utils/customHook/useSandreList'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import { SANDRE } from 'referencial/constants/ReferencialConstants'

const UnitIndicatorsChart = ({
    indicators = [],
}) => {
    const groupedIndicators = {
        [i18n.notDetermined]: [],
        [i18n.normale]: [],
        [i18n.vigilance]: [],
        [i18n.crisis]: [],
        [i18n.noData]: [],
        ...groupBy(indicators, 'value'),
    }

    const getIndicatorColor = (key) => {
        if (key === i18n.notDetermined) {
            return INDICATORS_COLORS.BLACK
        } else if (key === i18n.crisis) {
            return INDICATORS_COLORS.RED
        } else if (key === i18n.vigilance) {
            return INDICATORS_COLORS.YELLOW
        } else if (key === i18n.noData) {
            return INDICATORS_COLORS.GREY
        }
        return INDICATORS_COLORS.BLUE
    }

    const formattedData = keys(groupedIndicators).map(key => {
        const data = groupedIndicators[key]
        return {
            value: data.length,
            name: `${data.length} ${key}`,
            itemStyle: {
                color: getIndicatorColor(key),
            },
        }
    })

    const formatTooltip = ({ marker, data: { name = '' } }) => `${marker} ${name}`

    const option = {
        tooltip: {
            trigger: 'item',
            formatter: formatTooltip,
        },
        legend: {
            orient: 'vertical',
            top: 'center',
            right: '10%',
            itemWidth: 28,
            itemHeight: 17,
            textStyle: {
                width: 300,
                overflow: 'break',
                fontSize: 13,
                padding: [0, 0, 0, 10],
            },
        },
        series: [
            {
                type: 'pie',
                radius: ['35%', '85%'],
                avoidLabelOverlap: false,
                label: {
                    show: false,
                    position: 'center',
                },
                labelLine: {
                    show: false,
                },
                data: formattedData,
                right: '55%',
            },
        ],
    }

    return (
        <ReactECharts
            echarts={echarts}
            option={option}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '150px' }}
        />
    )
}

UnitIndicatorsChart.propTypes = {
    indicators: PropTypes.arrayOf(PropTypes.instanceOf(DtoObservatoryFollowData)),
}

const ProductionUnitsKeyFigures = ({
    units = [],
}) => {
    const {
        contributors,
        contributorLinks,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        contributorLinks: store.StationReducer.contributorLinks,
    }), shallowEqual)

    const now = moment().valueOf()
    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR

    const valuableContributors = contributorLinks.filter(c =>
        (c.endDate >= now || !c.endDate) &&
        units.some(u => u?.id === c.idStation) &&
        contributors.find(cr => c.idContributor === cr.id),
    ).map(c => ({ ...c, startDate: c.startDate ?? 0 }))

    const finalReferents = useMemo(() => {
        const referentsList = valuableContributors.filter(c => c.contributorType === referentType)
        const referentsGroupByStation = groupBy(referentsList, 'idStation')
        return values(keys(referentsGroupByStation).map(unit => maxBy(referentsGroupByStation[unit], 'startDate')))
    }, [valuableContributors, referentType])

    const finalOperators = useMemo(() => {
        const operatorsList = valuableContributors.filter(c => c.contributorType === operatorType)
        const operatorsGroupByStation = groupBy(operatorsList, 'idStation')
        return values(keys(operatorsGroupByStation).map(unit => maxBy(operatorsGroupByStation[unit], 'startDate')))
    }, [valuableContributors, operatorType])

    return (
        <Grid container alignItems='center' sx={{ height: '100%', fontWeight: '600' }}>
            <Grid size={10}>
                {i18n.distributionUnitsName}
            </Grid>
            <Grid size={2}>
                {units.length}
            </Grid>
            <Grid size={10}>
                {template(i18n.currentContributors)({ contributor: contributorsList.find(c => c.code === referentType)?.name })}
            </Grid>
            <Grid size={2}>
                {finalReferents.length}
            </Grid>
            <Grid size={10}>
                {template(i18n.currentContributors)({ contributor: contributorsList.find(c => c.code === operatorType)?.name })}
            </Grid>
            <Grid size={2}>
                {finalOperators.length}
            </Grid>
        </Grid>
    )
}

ProductionUnitsKeyFigures.propTypes = {
    units: PropTypes.arrayOf(PropTypes.shape({})),
}

const UnitIndicatorsPanel = ({
    indicators = [],
    productionUnits = [],
}) => {
    const {
        linkedPiezosPrel,
    } = useSelector(store => ({
        linkedPiezosPrel: store.ProductionUnitReducer.linkedPiezosPrel,
    }), shallowEqual)

    const getFormattedIndicators = (unitIndicators) => {
        if (!unitIndicators.length) {
            return { value: i18n.notDetermined, color: INDICATORS_COLORS.BLACK }
        } else if (unitIndicators.some(d => [
            'red',
            'indianred',
            'darkmagenta',
            THRESHOLD_COLORS_CODES.RED.toLowerCase(),
            THRESHOLD_COLORS_CODES.INDIANRED.toLowerCase(),
            THRESHOLD_COLORS_CODES.DARK_MAGENTA.toLowerCase(),
        ].includes(d.color?.toLowerCase() || '') || (d?.value?.toLowerCase() || '')?.includes('alerte'))) {
            return { value: i18n.crisis, color: INDICATORS_COLORS.RED }
        } else if (unitIndicators.some(d => [
            'yellow',
            'lightyellow',
            'orange',
            THRESHOLD_COLORS_CODES.YELLOW.toLowerCase(),
            THRESHOLD_COLORS_CODES.LIGHT_YELLOW.toLowerCase(),
            THRESHOLD_COLORS_CODES.ORANGE.toLowerCase(),
        ].includes(d.color?.toLowerCase() || '') || (d?.value?.toLowerCase() || '')?.includes('vigilance'))) {
            return { value: i18n.vigilance, color: INDICATORS_COLORS.YELLOW }
        } else if (unitIndicators.find(d => ['grey', 'gray'].includes(d.color?.toLowerCase()))) {
            return { value: i18n.noData, color: INDICATORS_COLORS.GREY }
        }
        return { value: i18n.normale, color: INDICATORS_COLORS.BLUE }
    }

    const indicatedUnits = useMemo(() => productionUnits.map(unit => indicators.filter(i => i.productionUnit === unit.name).flatMap(indic => indic.data)), [indicators, productionUnits])
    const formattedUnits = useMemo(() => indicatedUnits.map(u => getFormattedIndicators(u)), [indicatedUnits])

    const formattedUnitsCrisis = useMemo(() => {
        return indicatedUnits.flat().filter(indic => {
            const value = (indic?.value || '')?.toLowerCase()
            return [
                'red',
                'indianred',
                'darkmagenta',
                THRESHOLD_COLORS_CODES.RED.toLowerCase(),
                THRESHOLD_COLORS_CODES.INDIANRED.toLowerCase(),
                THRESHOLD_COLORS_CODES.DARK_MAGENTA.toLowerCase(),
                'yellow',
                'lightyellow',
                'orange',
                THRESHOLD_COLORS_CODES.YELLOW.toLowerCase(),
                THRESHOLD_COLORS_CODES.LIGHT_YELLOW.toLowerCase(),
                THRESHOLD_COLORS_CODES.ORANGE.toLowerCase(),
            ].includes(indic?.color?.toLowerCase() || '') || value.includes('alerte') || value.includes('vigilance')
        })
    }, [indicatedUnits])

    const groupedIndicatorsCrisis = countBy(formattedUnitsCrisis, d => d?.value.split(' : ')?.[0])
    const dispatchScreen = window.innerWidth >= 1280 ? 0 : 1

    return (
        <Grid container spacing={2} >
            {!!formattedUnits.length && (
                <>
                    <Grid size={4 + (linkedPiezosPrel.length ? dispatchScreen : 2)}>
                        <StyledFieldSet style={{ maxWidth: '500px', margin: 0, padding: '0 1rem' }}>
                            <StyledLegend>{i18n.statuts}</StyledLegend>
                            <UnitIndicatorsChart indicators={formattedUnits} />
                        </StyledFieldSet>
                    </Grid>
                    {!!linkedPiezosPrel.length && (
                        <Grid size={4 + dispatchScreen}>
                            <StyledFieldSet style={{ maxWidth: '500px', margin: 0, padding: '0 1rem' }}>
                                <StyledLegend>{i18n.annualVolumesWithdrawn}</StyledLegend>
                                <UnitLinkedVolumes />
                            </StyledFieldSet>
                        </Grid>
                    )}
                    {!!formattedUnitsCrisis.length && (
                        <Grid size={2 + (linkedPiezosPrel.length ? 0 : 1)} alignItems='center' sx={{ fontWeight: '600' }}>
                            <StyledFieldSet style={{ width: '100%', height: '100%', margin: 0, padding: '0 1rem' }}>
                                <StyledLegend>{i18n.overruns}</StyledLegend>
                                <Grid container alignItems='center' sx={{ height: '100%' }}>
                                    {keys(groupedIndicatorsCrisis).map(key => (
                                        <React.Fragment key={key}>
                                            <Grid size={10}>
                                                {key}
                                            </Grid>
                                            <Grid size={2}>
                                                {groupedIndicatorsCrisis[key]}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </StyledFieldSet>
                        </Grid>
                    )}
                    {(dispatchScreen === 0 || !formattedUnitsCrisis.length || !linkedPiezosPrel.length) && (
                        <Grid size={2 + (linkedPiezosPrel.length ? 0 : 1)}>
                            <StyledFieldSet sx={{ height: '100%', margin: 0, padding: '0 0 0 1rem' }}>
                                <StyledLegend>{i18n.keyFigures}</StyledLegend>
                                <ProductionUnitsKeyFigures units={productionUnits} />
                            </StyledFieldSet>
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    )
}

UnitIndicatorsPanel.propTypes = {
    indicators: PropTypes.arrayOf(PropTypes.instanceOf(DtoObservatoryFollowResult)),
    productionUnits: PropTypes.arrayOf(PropTypes.shape({})),
}

const UnitStats = ({
    productionUnits = [],
    parentIndicators = [],
}) => {
    const {
        productionUnitsAssociations,
    } = useSelector(store => ({
        productionUnitsAssociations: store.ProductionUnitReducer.productionUnitsAssociations,
    }), shallowEqual)

    const [qualitoIndicators, setQualitoIndicators] = useState([])

    const dispatch = useDispatch()

    const { isLoaded, progress } = useProgressDispatch(() => {
        if (!productionUnits.length) {
            return []
        }
        const associatedQualitos = productionUnitsAssociations.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.quality).map(({ stationLinkedId }) => stationLinkedId)

        return [
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedQualitos, STATION_TYPE_NAME.qualitometer)).then(newIndicators => setQualitoIndicators(newIndicators ?? [])),
        ]
    }, [])

    useEffect(() => {
        dispatch(ProductionUnitAction.fetchLinkedPiezosPrel(productionUnits.map(uaf => uaf.id)))
    }, [productionUnits])

    if (!isLoaded) {
        return <ProgressBar progress={progress} withMessage />
    }

    return (
        <UnitIndicatorsPanel
            indicators={[...parentIndicators, ...qualitoIndicators]}
            productionUnits={productionUnits}
        />
    )
}

UnitStats.propTypes = {
    productionUnits: PropTypes.arrayOf(PropTypes.shape({})),
    parentIndicators: PropTypes.arrayOf(PropTypes.instanceOf(DtoObservatoryFollowResult)),
}

const ProductionUnitStats = ({
    productionUnits,
    indicators = [],
}) => {
    const {
        defaultProductionUnits,
    } = useSelector(store => ({
        defaultProductionUnits: store.ProductionUnitReducer.productionUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    const productionUnitsFiltered = productionUnits || defaultProductionUnits

    const { isLoaded } = useProgressDispatch(() => [
        dispatch(ProductionUnitAction.fetchAllProductionUnitsAssociations()),
    ], [])

    return !!isLoaded && (
        <UnitStats productionUnits={productionUnitsFiltered} parentIndicators={indicators} />
    )
}

ProductionUnitStats.propTypes = {
    productionUnits: PropTypes.arrayOf(PropTypes.shape({})),
    indicators: PropTypes.arrayOf(PropTypes.instanceOf(DtoObservatoryFollowResult)),
}

export default ProductionUnitStats